import React, { useEffect, useState } from 'react';
import AddressManagementService, { Address, CorrectCoordinatesResponse } from '../../services/addressManagementService';

interface CorrectCoordinatesModalProps {
    isOpen: boolean;
    onClose: () => void;
    traceId: string;
    address: Address;
    correctedBy: string;
    onSuccess: (response: CorrectCoordinatesResponse) => void;
}

const CorrectCoordinatesModal: React.FC<CorrectCoordinatesModalProps> = ({
    isOpen,
    onClose,
    traceId,
    address,
    correctedBy,
    onSuccess
}) => {
    const [latitude, setLatitude] = useState<string>('');
    const [longitude, setLongitude] = useState<string>('');
    const [reason, setReason] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setLatitude(address.latitude != null ? String(address.latitude) : '');
            setLongitude(address.longitude != null ? String(address.longitude) : '');
            setReason('');
            setError(null);
        }
    }, [isOpen, address]);

    if (!isOpen) return null;

    const parsedLat = parseFloat(latitude);
    const parsedLng = parseFloat(longitude);
    const isValid = !isNaN(parsedLat) && !isNaN(parsedLng) &&
        parsedLat >= -90 && parsedLat <= 90 &&
        parsedLng >= -180 && parsedLng <= 180;

    const handleSubmit = async () => {
        if (!isValid) return;
        setIsSaving(true);
        setError(null);
        try {
            const response = await AddressManagementService.correctCoordinates(traceId, {
                latitude: parsedLat,
                longitude: parsedLng,
                correctedBy,
                reason: reason.trim() || undefined
            });
            onSuccess(response);
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || 'Failed to correct coordinates');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div
            className="modal fade show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
            tabIndex={-1}
            role="dialog"
            onClick={onClose}
        >
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content border-0 shadow-lg">
                    <div className="modal-header border-bottom">
                        <h5 className="modal-title">
                            <i className="bi bi-geo-alt me-2"></i>Correct Coordinates
                        </h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <div className="alert alert-info small py-2">
                            <i className="bi bi-info-circle-fill me-2"></i>
                            Updates the pin for this record in place (same Trace ID). The corrected
                            coordinates are treated as authoritative and will not be overwritten by refreshes.
                        </div>

                        <div className="mb-2 small text-muted">
                            {address.street1}{address.street2 ? `, ${address.street2}` : ''}, {address.city}, {address.stateCode} {address.zip}
                        </div>

                        <div className="row g-2 mb-3">
                            <div className="col-6">
                                <label className="form-label small fw-bold">Latitude *</label>
                                <input
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    value={latitude}
                                    onChange={(e) => setLatitude(e.target.value)}
                                    disabled={isSaving}
                                />
                            </div>
                            <div className="col-6">
                                <label className="form-label small fw-bold">Longitude *</label>
                                <input
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    value={longitude}
                                    onChange={(e) => setLongitude(e.target.value)}
                                    disabled={isSaving}
                                />
                            </div>
                        </div>

                        <div className="mb-2">
                            <label className="form-label small fw-bold">Reason</label>
                            <textarea
                                className="form-control"
                                rows={2}
                                maxLength={500}
                                placeholder="Why are the coordinates being corrected?"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                disabled={isSaving}
                            />
                        </div>

                        {error && (
                            <div className="alert alert-danger small py-2 mb-0">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>{error}
                            </div>
                        )}
                    </div>
                    <div className="modal-footer border-top">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSaving}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={!isValid || isSaving}>
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Correcting...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-lg me-2"></i>Correct Coordinates
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CorrectCoordinatesModal;
